import {
  AGENT_STATUS,
  DeliveryStatus,
  EncodeQueryParams,
  FETCH_CHATS_CALLBACK_TYPE,
  IEnrichCustomerSelfiePayload,
  IFetchChatsHelper,
  IFetchMoreChatsParams,
  IFetchNewChatsParams,
  IFetchUpdatedChatsParams,
  IGetCustomerSelfiePayload,
  MessageType,
  ReplySendingStatus,
  SendChatMessage,
  SendChatMessageWithImage,
  SortOrder
} from '../types';
import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from 'src/utils/ApiHelper';
import {
  addWhatsappChatbotMessage,
  appendNewWhatsappChatbotMessage,
  setIsAgentOnline,
  setIsFileUploaded,
  setNewWhatsappChatbotMessage,
  setReplySendingStatus,
  setSelectedChatLoanAndUserDetails,
  setShowErrorPage,
  setTemplateTriggeredDetails,
  setUpdateWhatsappChatbotMessage,
  setUploadedFileData
} from 'src/reducers/whatsappChatbotSlice';
import { PRODUCT_TYPE } from 'src/pages/CaseDetails/constants/MoreDetails.constant';
import { noop } from '@utils/common';
import {
  ECallingType,
  FeedbackPayload
} from '../../CaseDetails/feedbackForm/interfaces/index.type';
import { toast } from '@primitives/Toast';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { isFunction, throttleFunction } from '@cp/utils/commonUtils';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { WHATSAPP_SELFIE_THROTTLE_DURATION } from '../constants';
import { v4 as uuidv4 } from 'uuid';

const FETCH_CHATS_CALLBACK = {
  [FETCH_CHATS_CALLBACK_TYPE.NEW_CHATS]: setNewWhatsappChatbotMessage,
  [FETCH_CHATS_CALLBACK_TYPE.MORE_CHATS]: appendNewWhatsappChatbotMessage,
  [FETCH_CHATS_CALLBACK_TYPE.UPDATE_CHATS]: setUpdateWhatsappChatbotMessage
};

const encodeQueryParams = ({
  chatReferenceId,
  createdAt,
  sortOrder
}: EncodeQueryParams):
  | {
      chatReferenceId: string;
      lastEvaluatedKey: string;
      sortOrder: SortOrder;
    }
  | {
      chatReferenceId: string;
      sortOrder: SortOrder;
    } => {
  return createdAt
    ? {
        chatReferenceId,
        lastEvaluatedKey: btoa(createdAt.toString()),
        sortOrder: sortOrder || SortOrder.DESC
      }
    : {
        chatReferenceId,
        sortOrder: sortOrder || SortOrder.DESC
      };
};

export const markRead = ({ chatReferenceId }: { chatReferenceId: string }) => {
  const url = getApiUrl(ApiKeys.WHATSPPCHATBOT_MARK_MESSAGE_READ, null, { chatReferenceId });

  axiosInstance.post(url).then(() => {
    // TODO: Will Add Clickstream
  });
};

const fetchChatsHelper = ({
  chatReferenceId,
  createdAt,
  sortOrder,
  successCallbackDispatch,
  isCallMarkRead,
  failureCallback,
  dispatch
}: IFetchChatsHelper) => {
  const url = getApiUrl(
    ApiKeys.GET_WHATSAPPCHATBOT_MESSAGES,
    null,
    encodeQueryParams({ chatReferenceId, createdAt, sortOrder })
  );

  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      dispatch(
        FETCH_CHATS_CALLBACK[successCallbackDispatch]({
          referenceId: chatReferenceId,
          messages: res.data
        })
      );
      isCallMarkRead && markRead({ chatReferenceId });
    })
    .catch(error => {
      failureCallback && failureCallback();
      logError(error);
    });
};

export const fetchNewChats =
  ({ chatReferenceId, sortOrder, isCallMarkRead }: IFetchNewChatsParams) =>
  (dispatch: Dispatch) => {
    dispatch(setReplySendingStatus(ReplySendingStatus.LOADING));

    fetchChatsHelper({
      chatReferenceId,
      sortOrder,
      successCallbackDispatch: FETCH_CHATS_CALLBACK_TYPE.NEW_CHATS,
      isScrollBottom: true,
      isCallMarkRead,
      failureCallback: () => {
        dispatch(setShowErrorPage(true));
      },
      dispatch
    });
  };

export const loadMoreChats =
  ({ chatReferenceId, createdAt, sortOrder }: IFetchMoreChatsParams) =>
  (dispatch: Dispatch) => {
    fetchChatsHelper({
      chatReferenceId,
      createdAt,
      sortOrder,
      successCallbackDispatch: FETCH_CHATS_CALLBACK_TYPE.MORE_CHATS,
      dispatch
    });
  };

export const fetchUpdatedChats =
  ({ chatReferenceId, sortOrder, messages, isCallMarkRead }: IFetchUpdatedChatsParams) =>
  (dispatch: Dispatch) => {
    if (messages.length === 0) return;
    fetchChatsHelper({
      chatReferenceId,
      sortOrder,
      isCallMarkRead,
      successCallbackDispatch: FETCH_CHATS_CALLBACK_TYPE.UPDATE_CHATS,
      dispatch
    });
  };

export const sendChatMessage =
  ({
    date,
    chatSender,
    chatReferenceId,
    messageType,
    messageContent,
    agentReferenceId,
    setChatMessageContent,
    placeholderData,
    templateId
  }: SendChatMessage) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(
      messageType === MessageType.TEMPLATE
        ? ApiKeys.POST_WHATSAPPCHATBOT_TRIGGERED_MESSAGES
        : ApiKeys.POST_WHATSAPPCHATBOT_MESSAGES
    );
    const payload = {
      chatSender,
      chatReferenceId: chatReferenceId,
      messageType,
      messageContent,
      placeholderData,
      templateId
    };

    axiosInstance
      .post(url, payload, {
        headers: {
          'X-NAVI-PRINCIPAL': agentReferenceId,
          includeInUptimeMetrics: true
        }
      })
      .then(res => {
        dispatch(
          addWhatsappChatbotMessage({
            message: {
              createdAt: date,
              sender: chatSender,
              content: messageContent,
              deliveryStatus: DeliveryStatus.INITIATED,
              messageId: res?.data?.messageId,
              senderReferenceId: agentReferenceId
            },
            referenceId: chatReferenceId
          })
        );
        setChatMessageContent('');
      })
      .catch(() => {
        dispatch(
          addWhatsappChatbotMessage({
            createdAt: date,
            sender: chatSender,
            content: messageContent,
            deliveryStatus: DeliveryStatus.FAILED,
            messageId: uuidv4(),
            senderReferenceId: agentReferenceId
          })
        );
        setChatMessageContent('');
      });
  };

export const sendChatMessageWithImage =
  ({
    date,
    chatSender,
    chatReferenceId,
    messageType,
    messageContent,
    agentReferenceId,
    setChatMessageContent,
    mediaS3Path,
    mediaType,
    caption,
    mediaUrl,
    fileName,
    fileSize
  }: SendChatMessageWithImage) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.POST_WHATSAPPCHATBOT_MULTIMEDIA);
    const payload = {
      chatSender,
      chatReferenceId: chatReferenceId,
      messageType,
      messageContent,
      mediaS3Path,
      mediaType,
      caption,
      mediaUrl,
      fileName,
      fileSize
    };

    axiosInstance
      .post(url, payload, {
        headers: {
          'X-NAVI-PRINCIPAL': agentReferenceId
        }
      })
      .then(res => {
        dispatch(
          addWhatsappChatbotMessage({
            message: {
              createdAt: date,
              sender: chatSender,
              content: messageContent,
              deliveryStatus: DeliveryStatus.INITIATED,
              messageId: res?.data?.messageId,
              senderReferenceId: agentReferenceId,
              messageType: messageType ?? MessageType.IMAGE,
              fileName,
              fileSize
            },
            referenceId: chatReferenceId
          })
        );
      })
      .catch(() => {
        dispatch(
          addWhatsappChatbotMessage({
            createdAt: date,
            sender: chatSender,
            content: messageContent,
            deliveryStatus: DeliveryStatus.FAILED,
            messageId: uuidv4(),
            senderReferenceId: agentReferenceId,
            messageType: MessageType.IMAGE
          })
        );
      })
      .finally(() => {
        setChatMessageContent('');
        dispatch(setIsFileUploaded(false));
        dispatch(setUploadedFileData(null));
      });
  };

export const loadLoanAndUserDetails =
  ({ loanAccountNumber }: { loanAccountNumber: string }) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.WHATSAPPCHATBOT_LOAN_AND_USER_DETAILS, {
      loanAccountNumber
    });

    axiosInstance.get(url, { headers: { includeInUptimeMetrics: true } }).then(res => {
      dispatch(
        setSelectedChatLoanAndUserDetails({
          lan: loanAccountNumber,
          loanAmount: res?.data?.loanDetails?.loanAmount?.amount,
          phone: res?.data?.customerInfo?.phoneNumber,
          totalDue: res?.data?.loanDetails?.outstandingDueAmount?.amount,
          loanType: PRODUCT_TYPE[res?.data?.loanDetails?.productType],
          name: res?.data?.customerInfo?.name,
          candidateId: res?.data?.customerInfo?.customerReferenceId,
          currentDpd: res?.data?.loanDetails?.currentDpd
        })
      );
    });
  };

export const getCustomerSelfie = async ({
  customerReferenceId,
  loanAccountNumber
}: IGetCustomerSelfiePayload) => {
  const url = getApiUrl(ApiKeys.WHATSAPP_CHAT_SELFIE, { customerReferenceId, loanAccountNumber });
  try {
    const response = await axiosInstance.get(url, {
      retry: 0,
      responseType: 'blob',
      headers: { donotHandleError: true }
    });
    return response.data;
  } catch (error) {
    logError(error, 'Error fetching customer selfie');
    return null;
  }
};

export const submitChatFeedback = (
  loanaccountnumber: string,
  customerreferenceid: string,
  body: FeedbackPayload,
  successCallback?: () => void,
  failureCallback?: () => void
) => {
  const url =
    body.interactionType === ECallingType.WHATSAPP_CHAT_HRC
      ? ApiKeys.SUBMIT_CHAT_FEEDBACK
      : ApiKeys.SUBMIT_FEEDBACK;
  axiosInstance
    .post(getApiUrl(url), body, {
      headers: { customerreferenceid, loanaccountnumber, donotHandleError: true }
    })
    .then(res => {
      const { data } = res;
      if (body.interactionType === ECallingType.WHATSAPP_CHAT_HRC) {
        resolveChat(body.interactionMetadata?.chatReferenceId);
      }
      if (body.interactionType === ECallingType.WHATSAPP_TEXT) {
        addClickstreamEvent(
          CLICKSTREAM_EVENT_NAMES.LH_TELE_WA_CHAT_FEEDBACK_FORM_SUBMITTED_SUCCESSFULLY,
          {
            lan: loanaccountnumber,
            customerReferenceId: customerreferenceid,
            feedback: data?.metadata?.tag,
            interactionReferenceId: data?.referenceId,
            recipientPhoneNumber: body.recipientPhoneNumber,
            agentReferenceId: body.agentReferenceId
          }
        );
      }
      isFunction(successCallback) && successCallback();
      toast('Feedback submitted successfully', { type: 'success' });
    })
    .catch(err => {
      let errorMessage = 'Something went wrong';
      if (err?.code === 'ERR_BAD_REQUEST') {
        errorMessage = err?.response?.data?.message?.replace('error : ', '');
      }
      if (body.interactionType === ECallingType.WHATSAPP_TEXT) {
        addClickstreamEvent(
          CLICKSTREAM_EVENT_NAMES.LH_TELE_WA_CHAT_FEEDBACK_FORM_SUBMISSION_FAILED,
          {
            lan: loanaccountnumber,
            customerReferenceId: customerreferenceid,
            recipientPhoneNumber: body.recipientPhoneNumber,
            agentReferenceId: body.agentReferenceId
          }
        );
      }
      toast(errorMessage, { type: 'error' });
      isFunction(failureCallback) && failureCallback();
    });
};

export const resolveChat = (chatRefId = '') => {
  axiosInstance
    .post(getApiUrl(ApiKeys.RESOLVE_CHAT), null, {
      headers: { donotHandleError: true },
      params: { chatReferenceId: chatRefId }
    })
    .then(() => {
      toast('Feedback submitted successfully', { type: 'success' });
    });
};

export const agentOnlineToggleHandler =
  (status: AGENT_STATUS, logoutCallback?: () => void) => (dispatch: Dispatch) => {
    axiosInstance
      .post(
        getApiUrl(ApiKeys.SET_CHAT_AGENT_STATUS, { status }),
        {},
        {
          headers: { donotHandleError: true }
        }
      )
      .then(() => {
        dispatch(setIsAgentOnline(status === AGENT_STATUS.ONLINE));
        logoutCallback && logoutCallback();
      });
  };

export const getTemplateDetails = (agentReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_TEMPLATE_DETAILS);
  return axiosInstance
    .get(url, {
      //TODO: headers need to be removed later
      headers: {
        'X-NAVI-PRINCIPAL': agentReferenceId
      }
    })
    .then(res => {
      dispatch(setTemplateTriggeredDetails(res.data));
    });
};

export const uploadFileOnWhatsApp = async (
  file: File,
  chatReferenceId: string,
  agentReferenceId: string,
  loanAccountNumber: string
): Promise<GenericObject> => {
  const url = getApiUrl(
    ApiKeys.GET_UPLOADED_MULTIMEDIA_URL,
    { loanAccountNumber: loanAccountNumber },
    { chatReferenceId: chatReferenceId }
  );

  const formData = new FormData();
  formData.append('file', file);

  return await axiosInstance.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-NAVI-PRINCIPAL': agentReferenceId
    }
  });
};
